<template>
  <router-link class="ui card" :to="url">
    <sui-card-content class="compact">
      <sui-button-group class="right floated" size="mini">
        <sui-button :icon="localAccessIcon" data-inverted :data-tooltip="localAccessTitle" data-goal="SERVICE_CARD_ACCESS" v-if="account.limits.invite && access.tariffManagement" @click.stop.prevent="$modals.entityAccess.open(service)" />
        <sui-button :icon="remoteAccessIcon" data-inverted :data-tooltip="remoteAccessTitle" @click.stop.prevent="actions.serviceUpdate ? $modals.updateService.open(service, 'updatingRemoteAccess') : () => {}" />
      </sui-button-group>
      <sui-card-header>
        {{service.name}}
      </sui-card-header>
      <sui-card-meta :title="service.modified">
        modified {{momentFromNow(service.modified)}}
      </sui-card-meta>
    </sui-card-content>
    <sui-card-content :style="{background}">
      <Logo class="right floated logo" :entity="service" />
      <div class="service-info">
        <b>{{title}}&nbsp;{{service.version}}</b><br>
        <small v-if="!endpoint">
          <span v-show="service.remoteAccess && service.services.length">Web access for {{service.services.length}} services</span>
          <span v-show="!service.services.length || !service.remoteAccess">No web access</span>
        </small>
        <small v-if="endpoint">
          <a :href="endpoint" target="_blank" @click.stop>{{endpoint.replace(/https?:\/\//,'')}}</a>
        </small>
      </div>
    </sui-card-content>
    <sui-button-group class="attached" size="tiny" fluid>
      <sui-button icon="cog" title="Edit" data-goal="SERVICE_CARD_EDIT" @click.stop.prevent="$modals.updateService.open(service)" />
      <sui-dropdown :class="{disabled: inProgress || !actions.serviceUpdate}" button text="Update" icon="dropdown" @click.stop.prevent>
        <sui-dropdown-menu>
          <sui-dropdown-item v-for="action in actionsForApply" :key="action.value" :disabled="action.disabled" data-goal="SERVICE_CARD_UPDATE" @click.stop.prevent="doAction(action.value)">{{action.text}}</sui-dropdown-item>
        </sui-dropdown-menu>
      </sui-dropdown>
      <sui-dropdown button text="Containers" icon="dropdown" @click.stop.prevent>
        <sui-dropdown-menu>
          <sui-dropdown-item class="container-item" v-for="container in containers" :key="container.id">
            <span class="text">{{container.name}}</span>
            <i class="black inverted bordered terminal link small icon" :class="{disabled: !actions.serviceTerminal}" title="Terminal" data-goal="SERVICE_CARD_CONTAINER_TERMINAL" @click.stop.prevent="$modals.terminal.open(container)" />
            <i class="file alternate outline link large icon" :class="{disabled: !actions.serviceInfo}" title="Logs" data-goal="SERVICE_CARD_CONTAINER_LOGS" @click.stop.prevent="$modals.log.open(container)" />
          </sui-dropdown-item>
        </sui-dropdown-menu>
      </sui-dropdown>
      <sui-dropdown class="icon" icon="ellipsis vertical" button :disabled="inProgress" @click.stop.prevent>
        <sui-dropdown-menu>
          <sui-dropdown-item icon="stop" text="Stop" v-show="!isOffline" :disabled="!actions.serviceUpdate" data-goal="SERVICE_CARD_STOP" @click.stop.prevent="stop()" />
          <sui-dropdown-item icon="refresh" text="Restart" v-show="!isOffline" :disabled="!actions.serviceUpdate" data-goal="SERVICE_CARD_RESTART" @click.stop.prevent="restart()" />
          <sui-dropdown-item icon="play" text="Start" v-show="isOffline" :disabled="!actions.serviceUpdate" data-goal="SERVICE_CARD_START" @click.stop.prevent="start()" />
          <sui-dropdown-divider />
          <sui-dropdown-item icon="file alternate outline" text="Log" :disabled="!actions.serviceInfo" data-goal="SERVICE_CARD_LOGS" @click.stop.prevent="$modals.log.open(service)" />
          <sui-dropdown-item icon="red trash" text="Destroy" :disabled="!actions.serviceDestroy" data-goal="SERVICE_CARD_DESTROY" @click.stop.prevent="destroy()" />
        </sui-dropdown-menu>
      </sui-dropdown>
    </sui-button-group>
    <EntityStatus :entity="service" extended />
  </router-link>
</template>

<script>
  import {mapGetters} from 'vuex'
  import EntityStatus from '@/components/EntityStatus'
  import utils from '@/utils'

  export default {
    name: 'ServiceCard',
    components: {EntityStatus},
    props: {
      service: {
        type: Object,
        required: true,
      },
    },
    data() {
      const hasSourceURL = this.service.source && this.service.source.url
      return {
        actionsForApply: [
          {value: 'VERSION', text: 'Update version of docker image', disabled: false},
          {value: 'REBUILD', text: 'Update global dependencies', disabled: false},
          {value: 'REDEPLOY', text: 'Update sources and local dependencies', disabled: !(this.service.deployCommands || hasSourceURL)},
          {value: 'SYNC_SOURCES', text: 'Update only sources', disabled: !hasSourceURL},
        ],
      }
    },
    computed: {
      ...mapGetters(['access', 'actionsById', 'account', 'config', 'member']),
      actions() {
        return this.actionsById[this.service.id]
      },
      containers() {
        return this.service._containers
      },
      url() {
        return this.service._path
      },
      title() {
        return this.service._typeTitle
      },
      background() {
        return this.config.colorCards ? `linear-gradient(135deg, transparent, ${this.service.constructor.color}22)` : 'transparent'
      },
      isOffline() {
        return this.service._isOffline
      },
      inProgress() {
        return this.service._inProgress
      },
      hasLocalRules() {
        return this.service.rules.local && Object.keys(this.service.rules.local).length
      },
      localAccessIcon() {
        return this.hasLocalRules ? 'blue users' : 'users'
      },
      localAccessTitle() {
        return this.hasLocalRules ? 'Has local access rules' : 'No local access rules'
      },
      remoteAccessIcon() {
        return this.service.remoteAccess ? 'blue globe' : 'shield'
      },
      remoteAccessTitle() {
        return this.service.remoteAccess ? 'Remote access from the Internet' : 'Secure private network'
      },
      endpoint() {
        return this.service._endpoint
      },
    },
    methods: {
      start() {
        this.service.start().catch(() => {})
      },
      restart() {
        return this.$modals.confirm.open({
          callback: () => {
            this.service.restart().catch(() => {})
          },
        })
      },
      stop() {
        return this.$modals.confirm.open({
          callback: () => {
            this.service.stop().catch(() => {})
          },
        })
      },
      destroy() {
        return this.$modals.confirm.open({
          callback: () => {
            utils.reachGoal('SERVICE_CARD_DESTROY_TRY')
            this.service.destroy().then(() => {
              utils.reachGoal('SERVICE_CARD_DESTROY_SUCCESS')
            }).catch(() => {
              utils.reachGoal('SERVICE_CARD_DESTROY_FAIL')
            })
          },
        })
      },
      doAction(action) {
        const text = this.actionsForApply.find(item => item.value === action).text.toLowerCase()
        return this.$modals.confirm.open({
          text: `You are going to ${text}.`,
          callback: () => {
            const actionsForApply = []
            switch(action) {
              case 'VERSION':
                actionsForApply.push('DOCKER_FORCE_REBUILD', 'REBUILD')
                break
              case 'REBUILD':
                actionsForApply.push('REBUILD')
                break
              case 'REDEPLOY':
                actionsForApply.push('SYNC_SOURCES', 'REDEPLOY')
                break
              case 'SYNC_SOURCES':
                actionsForApply.push('SYNC_SOURCES')
                break
            }
            this.service.applyActions(actionsForApply)
          },
        })
      },
    },
  }
</script>

<style scoped lang="postcss">
  .service-info { font-size: 1.1em; line-height: 1.2em; white-space: nowrap; overflow: hidden; }
  .right.floated.logo { margin-bottom: 0!important; height: 2.7em; }
  .containers-title { display: inline-block; margin: .3em 0}
  .label.disabled { opacity: .2!important; }
  .dropdown { text-align: center!important; border-radius: 0!important; }
  .menu { max-height: 20em; overflow-y: auto; }
  .container-item { display: flex!important; align-items: center; cursor: auto!important; }
  .container-item .text { flex: 1; margin-right: 2em; }
  .container-item .icon { margin: 0 0 0 .2em!important; border-radius: .3em; }
  .container-item .icon.terminal:hover { background-color: #2185D0!important; }
  .container-item .icon.file:hover { color: #2185D0!important; }
  .right.floated.buttons { position: absolute; top: 0; right: 0; }
  .right.floated.buttons .button { margin-left: -1em; padding-right: 1.8em!important; border-radius: 0; border-bottom-left-radius: 1em; border-bottom: 1px solid #eee; border-left: 1px solid #eee; background-color: #f3f3f3; box-shadow: none!important; }
  .right.floated.buttons .button:hover { background-color: #eee; }
  .right.floated.buttons .button:last-child { padding-right: 0.78571429em!important; }
  .content:first-child > .right.floated.buttons .button:last-child { border-top-right-radius: 0.28571429rem; }
  .right.floated.buttons .button >>> .icon { margin-top: -1px!important; }
</style>
